import { memoriesOf, memorySortTime } from 'helpers/memories';
import { byIdOrder } from 'helpers/memory-order';
import { AlbumsSortBy, AlbumContentType } from 'common/enums';
import { Album } from 'models/album';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { SharedByUser } from 'networking/types/shared-album';

// A memory plus the album it came from, so a flattened list can still open the
// right album and attribute shared entries.
export type MemoryItem = AlbumContentSerialized & {
  albumId: number;
  sharedBy?: SharedByUser;
  // Lives in the vault's hidden default album, i.e. isn't in any album.
  unsorted?: boolean;
};

const memoryTime = (memory: MemoryItem) => memorySortTime(memory);

// When the memory was added to the vault, whatever date it's set to.
const addedTime = (memory: MemoryItem) =>
  new Date(memory.createdAt).getTime() || 0;

// Flattens every album's contents into a single list of memories.
export function flattenMemories(
  albums: Album[],
  getSharedBy?: (album: Album) => SharedByUser | undefined,
): MemoryItem[] {
  return albums.flatMap((album) =>
    memoriesOf(album).map((content) => ({
      ...content,
      albumId: album.id,
      sharedBy: getSharedBy?.(album),
      unsorted: !!album.isDefault,
    })),
  );
}

export function filterMemoriesBySearch(
  list: MemoryItem[],
  query: string,
): MemoryItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter((memory) => memory.name?.toLowerCase().includes(q));
}

// `customFilters` is the same content-type map the Albums tab uses; null means
// "no filter applied".
export function filterMemoriesByType(
  list: MemoryItem[],
  customFilters: Record<AlbumContentType, boolean> | null,
): MemoryItem[] {
  if (customFilters === null) return list;
  const allowed = new Set(
    (Object.entries(customFilters) as [AlbumContentType, boolean][])
      .filter(([, checked]) => checked)
      .map(([type]) => type),
  );
  if (allowed.size === 0) return [];
  return list.filter((memory) =>
    allowed.has(memory.contentType as AlbumContentType),
  );
}

// `memoryOrder` is the vault's custom order (ids, first to last), used by
// AlbumsSortBy.custom; memories added since follow it, newest first.
export function sortMemories(
  list: MemoryItem[],
  sortBy: AlbumsSortBy | null,
  memoryOrder: number[] = [],
): MemoryItem[] {
  const sorted = [...list];
  switch (sortBy) {
    case AlbumsSortBy.custom:
      return byIdOrder(
        sorted.sort((a, b) => memoryTime(b) - memoryTime(a)),
        memoryOrder,
      );
    case AlbumsSortBy.oldestActivity:
      return sorted.sort((a, b) => memoryTime(a) - memoryTime(b));
    case AlbumsSortBy.recentlyAdded:
      return sorted.sort((a, b) => addedTime(b) - addedTime(a));
    case AlbumsSortBy.earliestAdded:
      return sorted.sort((a, b) => addedTime(a) - addedTime(b));
    case AlbumsSortBy.aToZ:
      return sorted.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
        }),
      );
    case AlbumsSortBy.zToA:
      return sorted.sort((a, b) =>
        (b.name || '').localeCompare(a.name || '', undefined, {
          sensitivity: 'base',
        }),
      );
    case AlbumsSortBy.recentActivity:
    default:
      return sorted.sort((a, b) => memoryTime(b) - memoryTime(a));
  }
}
